import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Animated,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import {
  registerUser,
  loginUser,
  loginWithGoogle,
  continueAsGuest,
  resetLoading,
  clearError,
} from '../../src/redux/authSlice';
import type { AuthErrorPayload } from '../../src/redux/authSlice';
import { AppDispatch, RootState } from '../../src/redux/store';
import { selectOnboardingCompleted } from '../../src/redux/onboardingSlice';
import { checkEmailExists, sendPasswordReset } from '../../src/services/authService';
import { readPendingInvite, resolvePostAuthRoute } from '../../src/utils/pendingInvite';
import { resolveReturnRoute } from '../../src/utils/postAuthReturn';
import { trackEvent } from '../../src/services/analyticsService';
import { Paprika } from '../../src/styles/theme';
import {
  BrandPanel,
  ReasonsList,
  AuthField,
  PrimaryButton,
  GoogleButton,
  Divider,
  AlertBanner,
  SuccessPanel,
  type AlertTone,
} from '../../src/components/auth';

const DESKTOP_BREAKPOINT = 820;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'email' | 'password' | 'create' | 'success';

type BannerState = {
  tone: AlertTone;
  icon: React.ComponentProps<typeof Feather>['name'];
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
} | null;

/** Entrance fade+lift, re-run on each step change via `key`. */
const StepFade: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, {
      toValue: 1,
      duration: 300,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [anim]);
  return (
    <Animated.View
      style={{
        opacity: anim,
        transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [6, 0] }) }],
      }}
    >
      {children}
    </Animated.View>
  );
};

const AuthScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const onboardingComplete = useSelector(selectOnboardingCompleted);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<BannerState>(null);
  const [checking, setChecking] = useState(false);
  const [locked, setLocked] = useState(false);
  const [googleRetry, setGoogleRetry] = useState(false);

  // True while THIS screen is driving an auth attempt, so the arrival-redirect
  // effect below doesn't fire on success and skip the success step.
  const inFlowRef = useRef(false);

  const isDesktop = width > DESKTOP_BREAKPOINT;

  // Someone who got here from an invite link is not here to reach the app —
  // they are here to finish joining a household. `next` is the in-memory hop;
  // the stash is what survives a Google signInWithRedirect, which reloads the
  // page. `inviteChecked` exists because the stash read is async and the
  // arrival bounce below is not: without the gate the bounce wins the race and
  // the invite is dropped, which is the bug this screen is being fixed for.
  const { next } = useLocalSearchParams<{ next?: string }>();
  const [joinRoute, setJoinRoute] = useState<string | null>(null);
  const [inviteChecked, setInviteChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stashed = await readPendingInvite();
      if (cancelled) return;
      // An invite always wins; otherwise go back to the screen that asked the
      // guest to sign up (the scan they wanted), when it is one we allow.
      setJoinRoute(resolvePostAuthRoute(next, stashed) ?? resolveReturnRoute(next));
      setInviteChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [next]);

  /** Where an authenticated user goes from here. */
  const goAfterAuth = () => router.replace((joinRoute ?? '/(app)') as any);

  useEffect(() => {
    dispatch(resetLoading());
    dispatch(clearError());
  }, [dispatch]);

  // Bounce an ALREADY-authenticated visitor who lands on this screen straight
  // to the app — or back to the invite they came in on. `inFlowRef` guards the
  // in-screen sign-in/registration path so a successful attempt shows the
  // success step instead of redirecting here.
  useEffect(() => {
    if (!inviteChecked) return;
    if (isAuthenticated && user && !inFlowRef.current) {
      goAfterAuth();
    }
  }, [isAuthenticated, user, router, inviteChecked, joinRoute]);

  const offlineBanner = (retry: () => void): BannerState => ({
    tone: 'danger',
    icon: 'wifi-off',
    title: t('auth.errOfflineTitle'),
    message: t('auth.errOfflineMsg'),
    actionLabel: t('auth.retry'),
    onAction: retry,
  });

  const resetToEmail = () => {
    setStep('email');
    setPassword('');
    setFieldError(null);
    setBanner(null);
    setLocked(false);
  };

  const handleReset = async () => {
    try {
      const channel = await sendPasswordReset(email, i18n.language);
      trackEvent('password_reset_requested', { channel });
    } catch (err) {
      // Never reveal outcome on screen — always the same neutral confirmation.
      // The code alone goes to the replay: user-not-found is routine here.
      console.warn('[auth] password reset not sent', (err as { code?: string })?.code);
      trackEvent('password_reset_requested', { channel: 'none' });
    }
    setLocked(false);
    setFieldError(null);
    setBanner({
      tone: 'info',
      icon: 'mail',
      title: t('auth.resetSentTitle'),
      message: t('auth.resetSentMsg'),
    });
  };

  const handleGoogle = async () => {
    setFieldError(null);
    setBanner(null);
    inFlowRef.current = true;
    try {
      await dispatch(loginWithGoogle()).unwrap();
      setGoogleRetry(false);
      setStep('success');
    } catch (payload) {
      inFlowRef.current = false;
      const { code, message } = (payload as AuthErrorPayload) ?? {};
      if (code === 'google-cancelled') {
        setGoogleRetry(true);
        setBanner({
          tone: 'info',
          icon: 'info',
          title: t('auth.errGoogleCancelledTitle'),
          message: t('auth.errGoogleCancelledMsg'),
        });
      } else if (code === 'network') {
        setBanner(offlineBanner(handleGoogle));
      } else {
        console.error('[auth] Google sign-in failed', code, message);
        // `message` is Firebase's own English text; the code is all a cook needs.
        setBanner({ tone: 'danger', icon: 'alert-triangle', title: t('auth.errGeneric') });
      }
    }
  };

  const handleEmailContinue = async () => {
    setFieldError(null);
    setBanner(null);
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setFieldError(t('auth.errEmailFormat'));
      return;
    }
    setChecking(true);
    try {
      const exists = await checkEmailExists(trimmed);
      setPassword('');
      setStep(exists ? 'password' : 'create');
    } catch (err) {
      // Treated as offline on screen; the replay keeps the real reason.
      console.warn('[auth] email check failed', err);
      setBanner(offlineBanner(handleEmailContinue));
    } finally {
      setChecking(false);
    }
  };

  const handlePasswordSubmit = async () => {
    setFieldError(null);
    setBanner(null);
    if (!password) {
      setFieldError(t('auth.validationPasswordRequired'));
      return;
    }
    inFlowRef.current = true;
    try {
      await dispatch(loginUser({ email: email.trim(), password })).unwrap();
      setStep('success');
    } catch (payload) {
      inFlowRef.current = false;
      const { code, message } = (payload as AuthErrorPayload) ?? {};
      if (code === 'bad-credentials') {
        setFieldError(t('auth.errBadPassword'));
      } else if (code === 'too-many-requests') {
        setLocked(true);
        setBanner({
          tone: 'warn',
          icon: 'alert-triangle',
          title: t('auth.errLockedTitle'),
          message: t('auth.errLockedMsg'),
          actionLabel: t('auth.resetNow'),
          onAction: handleReset,
        });
      } else if (code === 'network') {
        setBanner(offlineBanner(handlePasswordSubmit));
      } else {
        console.error('[auth] password sign-in failed', code, message);
        setFieldError(t('auth.errGeneric'));
      }
    }
  };

  const handleCreateSubmit = async () => {
    setFieldError(null);
    setBanner(null);
    if (!password || password.length < 6) {
      setFieldError(t('auth.validationPasswordLength'));
      return;
    }
    inFlowRef.current = true;
    try {
      await dispatch(registerUser({ email: email.trim(), password })).unwrap();
      setStep('success');
    } catch (payload) {
      inFlowRef.current = false;
      const { code, message } = (payload as AuthErrorPayload) ?? {};
      if (code === 'weak-password') {
        setFieldError(t('auth.validationPasswordLength'));
      } else if (code === 'email-in-use') {
        // Rare race (account created between check and submit): route to sign-in.
        setPassword('');
        setStep('password');
      } else if (code === 'network') {
        setBanner(offlineBanner(handleCreateSubmit));
      } else {
        console.error('[auth] account creation failed', code, message);
        setFieldError(t('auth.errGeneric'));
      }
    }
  };

  const handleGuest = () => {
    dispatch(continueAsGuest());
    router.replace(onboardingComplete ? '/(app)' : '/onboarding');
  };

  // ─── step content ───
  const backRow = (
    <TouchableOpacity onPress={resetToEmail} style={styles.backRow} hitSlop={6}>
      <Feather name="chevron-left" size={16} color={Paprika.textMuted} />
      <Text style={styles.backText}>{t('navigation.back')}</Text>
    </TouchableOpacity>
  );

  const emailChip = (
    <View style={styles.chip}>
      <View style={styles.chipLeft}>
        <Feather name="mail" size={16} color={Paprika.textMuted} />
        <Text style={styles.chipEmail} numberOfLines={1}>
          {email.trim()}
        </Text>
      </View>
      <TouchableOpacity onPress={resetToEmail} hitSlop={6}>
        <Text style={styles.chipChange}>{t('auth.change')}</Text>
      </TouchableOpacity>
    </View>
  );

  let content: React.ReactNode;
  if (step === 'success') {
    content = <SuccessPanel onOpen={goAfterAuth} />;
  } else if (step === 'email') {
    content = (
      <View>
        <Text style={styles.h1}>{t('auth.continueTitle')}</Text>
        {banner ? <View style={styles.bannerWrap}><AlertBanner {...banner} /></View> : null}
        <View style={styles.stack}>
          <GoogleButton
            label={googleRetry ? t('auth.retryGoogle') : t('auth.continueWithGoogle')}
            onPress={handleGoogle}
            loading={loading}
          />
          <Divider label={t('auth.or')} />
          <AuthField
            icon="mail"
            label={t('auth.emailLabel')}
            value={email}
            onChangeText={setEmail}
            error={fieldError}
            keyboardType="email-address"
            autoFocus
            onSubmitEditing={handleEmailContinue}
          />
          <PrimaryButton
            label={t('auth.continue')}
            onPress={handleEmailContinue}
            disabled={!email.includes('@')}
            loading={checking}
          />
        </View>
        <TouchableOpacity onPress={handleGuest} style={styles.guestLink} hitSlop={6}>
          <Text style={styles.guestLinkText}>{t('auth.guestLink')}</Text>
        </TouchableOpacity>
      </View>
    );
  } else if (step === 'password') {
    content = (
      <View>
        {backRow}
        <Text style={styles.h1}>{t('auth.welcomeBack')}</Text>
        {emailChip}
        {banner ? <View style={styles.bannerWrap}><AlertBanner {...banner} /></View> : null}
        <View style={styles.stack}>
          <AuthField
            icon="lock"
            label={t('auth.password')}
            value={password}
            onChangeText={setPassword}
            password
            error={fieldError}
            editable={!locked}
            autoFocus
            onSubmitEditing={handlePasswordSubmit}
          />
          <PrimaryButton
            label={t('auth.signIn')}
            onPress={handlePasswordSubmit}
            loading={loading}
            disabled={locked}
          />
        </View>
        <TouchableOpacity onPress={handleReset} style={styles.centerLink} hitSlop={6}>
          <Text style={styles.centerLinkText}>
            {fieldError ? t('auth.resetMyPassword') : t('auth.forgotPassword')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  } else {
    // create
    content = (
      <View>
        {backRow}
        <Text style={styles.h1}>{t('auth.createTitle')}</Text>
        {emailChip}
        {banner ? <View style={styles.bannerWrap}><AlertBanner {...banner} /></View> : null}
        <View style={styles.stack}>
          <AuthField
            icon="lock"
            label={t('auth.choosePassword')}
            value={password}
            onChangeText={setPassword}
            password
            error={fieldError}
            autoFocus
            onSubmitEditing={handleCreateSubmit}
          />
          <PrimaryButton
            label={t('auth.createMyAccount')}
            onPress={handleCreateSubmit}
            loading={loading}
          />
        </View>
        <Text style={styles.legalFine}>{t('auth.legalFine')}</Text>
      </View>
    );
  }

  const formColumn = (
    <View style={styles.formInner}>
      {!isDesktop && step === 'email' ? <ReasonsList variant="header" /> : null}
      <StepFade key={step}>{content}</StepFade>
    </View>
  );

  if (isDesktop) {
    return (
      <View style={styles.desktopShell}>
        <View style={styles.brandCol}>
          <BrandPanel />
        </View>
        <View style={styles.formColDesktop}>{formColumn}</View>
      </View>
    );
  }

  return (
    <View style={styles.mobileShell}>
      <ScrollView
        contentContainerStyle={styles.mobileScroll}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {formColumn}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  desktopShell: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: Paprika.background,
  },
  brandCol: {
    flex: 0.92,
  },
  formColDesktop: {
    flex: 1.08,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  mobileShell: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  mobileScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  formInner: {
    width: '100%',
    maxWidth: 380,
    alignSelf: 'center',
  },
  h1: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 30,
    lineHeight: 33,
    letterSpacing: -0.3,
    color: Paprika.textPrimary,
    marginBottom: 22,
  },
  stack: {
    gap: 14,
  },
  bannerWrap: {
    marginBottom: 16,
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
    marginLeft: -6,
    paddingVertical: 8,
    paddingHorizontal: 6,
    alignSelf: 'flex-start',
  },
  backText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    color: Paprika.textMuted,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 12,
    marginBottom: 18,
  },
  chipLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    flex: 1,
    minWidth: 0,
  },
  chipEmail: {
    flex: 1,
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textPrimary,
  },
  chipChange: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: Paprika.accent,
  },
  centerLink: {
    alignSelf: 'center',
    marginTop: 12,
    paddingVertical: 4,
  },
  centerLinkText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.accent,
  },
  guestLink: {
    alignSelf: 'center',
    marginTop: 20,
    paddingVertical: 6,
  },
  guestLinkText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    color: Paprika.textMuted,
  },
  legalFine: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    lineHeight: 16,
    color: Paprika.textMuted,
    textAlign: 'center',
    marginTop: 14,
  },
});

export default AuthScreen;
